import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Trash2, Reply, ArrowUpRight } from "lucide-react";

function fileUrl(path) {
  const token = localStorage.getItem("rr_token");
  return `${API}/files/${path}?auth=${encodeURIComponent(token || "")}`;
}

export default function AdminComments() {
  const [comments, setComments] = useState([]);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/comments");
    setComments(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    await api.delete(`/comments/${cid}`);
    setComments((cs) => cs.filter((c) => c.id !== cid));
    toast.success("Deleted.");
  };

  const sendReply = async (planId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/meal-plans/${planId}/comments`, { text: replyText.trim(), parent_id: replyOpen });
      setReplyOpen(null); setReplyText("");
      toast.success("Reply posted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-14" data-testid="admin-comments">
      <header>
        <div className="label-eyebrow mb-3">Admin · Manage Comments</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight">Every voice, in <em className="italic text-[hsl(var(--primary))]">one place</em>.</h1>
        <p className="mt-6 text-stone-600 max-w-xl leading-relaxed">Read, reply, and moderate — across every published plan.</p>
      </header>

      {comments.length === 0 ? <p className="text-stone-500 italic font-serif text-xl">No comments yet.</p> : (
        <div className="space-y-8 border-t border-border pt-8" data-testid="admin-comments-list">
          {comments.map((c) => (
            <div key={c.id} className="grid md:grid-cols-12 gap-6 border-b border-border pb-8" data-testid={`ac-${c.id}`}>
              <div className="md:col-span-3">
                <div className="label-eyebrow mb-1">{c.plan_month}</div>
                <div className="font-serif text-lg">{c.plan_title}</div>
                <Link to={`/app/plans/${c.plan_id}`} className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1 mt-2">Open plan <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} /></Link>
              </div>
              <div className="md:col-span-9">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-serif text-lg">{c.user_name}</span>
                  {c.is_admin && <span className="text-xs bg-[hsl(var(--secondary))] text-white px-2 py-0.5">Admin</span>}
                  <span className="text-xs text-stone-400">{new Date(c.created_at).toLocaleString()}</span>
                  <span className="text-xs text-stone-500 inline-flex items-center gap-1"><Heart className="w-3 h-3" strokeWidth={1.5} />{c.likes}</span>
                </div>
                <p className="text-base leading-relaxed whitespace-pre-wrap">{c.text}</p>
                {c.photo_paths?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {c.photo_paths.map((p) => <img key={p} src={fileUrl(p)} alt="" className="w-16 h-16 object-cover border border-border" />)}
                  </div>
                )}
                <div className="flex items-center gap-4 mt-4">
                  <button onClick={() => { setReplyOpen(c.id); setReplyText(""); }} data-testid={`reply-${c.id}`} className="text-xs text-stone-500 hover:text-[hsl(var(--primary))] inline-flex items-center gap-1">
                    <Reply className="w-3.5 h-3.5" strokeWidth={1.5} /> Reply
                  </button>
                  <button onClick={() => remove(c.id)} data-testid={`admin-del-c-${c.id}`} className="text-xs text-stone-400 hover:text-[hsl(var(--destructive))] inline-flex items-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
                  </button>
                </div>
                {replyOpen === c.id && (
                  <div className="mt-4 border border-border p-4">
                    <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={3} data-testid={`reply-input-${c.id}`} placeholder="Reply as admin…" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus resize-none" />
                    <div className="flex justify-end gap-2 mt-3">
                      <button onClick={() => setReplyOpen(null)} className="text-xs text-stone-500 px-4 py-2">Cancel</button>
                      <button onClick={() => sendReply(c.plan_id)} data-testid={`send-reply-${c.id}`} className="text-xs bg-[hsl(var(--foreground))] text-white px-4 py-2 hover:bg-[hsl(var(--primary))]">Send reply</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
